/**
 * /academy/course/review — the spaced review of the day: the exercises missed before come back
 * (after 1, 3, 7, 14 and 30 days); each one is corrected at once.
 */

import {
  academyCourseReviewCreate,
  getAcademyCourseRetrieveQueryKey,
  getAcademyCourseReviewRetrieveQueryKey,
  useAcademyCourseReviewRetrieve,
} from "@agencyos/api-client";
import { type Feedback, parseFeedback, parseReviewToday } from "@agencyos/core";
import { Button, EmptyState, GlassCard, Skeleton, buttonClass } from "@agencyos/ui-web";
import { useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, ArrowRight, PartyPopper, Repeat } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { PageHead } from "../ui";
import { ContentLangProvider } from "./contentLang";
import { useCourseExam, useCourseText } from "./courseText";
import { type ExamCode, GOETHE_CODE, TEF, courseExam } from "./exam";
import { ExerciseCard } from "./Exercise";

export function ReviewPage() {
  const exam = useCourseExam();
  return <ReviewView key={exam} exam={courseExam(exam)} />;
}

function ReviewView({ exam }: { exam: ExamCode }) {
  const { t } = useTranslation();
  const { ct, contentLang } = useCourseText(exam);
  const queryClient = useQueryClient();
  const query = useAcademyCourseReviewRetrieve({ exam }, { query: { retry: false, staleTime: Infinity } });
  const data = useMemo(() => (query.data?.status === 200 ? parseReviewToday(query.data.data) : null), [query.data]);
  const [index, setIndex] = useState(0);
  const [results, setResults] = useState<Record<string, Feedback>>({});

  const back = (
    <Link to="/academy/course" className="ac-back">
      <ArrowLeft aria-hidden size={16} />
      {ct("lesson.toPath")}
    </Link>
  );

  if (query.isLoading) return <Skeleton style={{ height: 320 }} />;
  if (!data) {
    return (
      <div className="ac-page">
        {back}
        <GlassCard className="ac-card ac-card--center">
          <p className="ko">{t("common.error")}</p>
        </GlassCard>
      </div>
    );
  }
  const exercises = data.exercises;
  const item = exercises[index];
  const right = Object.values(results).filter((r) => r.correct).length;

  const check = async (exercise: string, response: unknown) => {
    const answer = await academyCourseReviewCreate({ exercise, response }, exam === TEF || exam === GOETHE_CODE ? { exam } : undefined);
    const feedback = parseFeedback(answer.data);
    setResults((prev) => ({ ...prev, [exercise]: feedback }));
    return feedback;
  };
  const next = () => {
    setIndex(index + 1);
    if (index + 1 >= exercises.length) {
      void queryClient.invalidateQueries({ queryKey: getAcademyCourseRetrieveQueryKey({ exam }) });
      void queryClient.invalidateQueries({ queryKey: getAcademyCourseReviewRetrieveQueryKey({ exam }) });
    }
  };

  return (
    <ContentLangProvider lang={contentLang}>
    <div className="ac-page ac-lesson">
      {back}
      <PageHead eyebrow={ct("review.eyebrow")} title={ct("review.title")} subtitle={<p>{ct("review.intro")}</p>} />
      {exercises.length === 0 ? (
        <GlassCard className="ac-card">
          <EmptyState icon={<Repeat aria-hidden size={22} />} title={ct("review.noneTitle")} description={ct("review.noneText")} />
        </GlassCard>
      ) : !item ? (
        <GlassCard className="ac-card">
          <EmptyState
            icon={<PartyPopper aria-hidden size={22} />}
            title={ct("review.doneTitle")}
            description={ct("review.doneText", { right, total: exercises.length })}
            actions={
              <Link className={buttonClass("primary")} to="/academy/course">
                {ct("lesson.toPath")}
              </Link>
            }
          />
        </GlassCard>
      ) : (
        <section className="ac-lesson__practice" aria-label={ct("review.title")}>
          <div className="ac-lesson__bar">
            <p className="ac-question__count ao-num">{ct("lesson.exerciseOf", { n: index + 1, total: exercises.length })}</p>
            <div className="ac-progress" aria-hidden="true">
              <span style={{ transform: `scaleX(${index / exercises.length})` }} />
            </div>
          </div>
          <div className="ac-question__sheet">
            <ExerciseCard key={item.id} item={item} onCheck={(response) => check(item.id, response)} />
          </div>
          {results[item.id] && (
            <div className="ac-question__actions">
              <Button size="lg" onClick={next}>
                {ct("lesson.nextExercise")}
                <ArrowRight aria-hidden size={18} />
              </Button>
            </div>
          )}
        </section>
      )}
    </div>
    </ContentLangProvider>
  );
}
